/**
 * Modal — the shared accessible dialog primitive for the app's confirm/entry
 * dialogs. Factored out of the COPPA AgeGateModal pattern (the one hand-rolled
 * dialog that got the whole checklist right) so every dialog shares ONE
 * focus-trap + restore-focus + Escape + initial-focus implementation instead of
 * each re-deriving it (and diverging).
 *
 * ACCESSIBILITY (WCAG 2.4.3 Focus Order / 4.1.2 Name-Role-Value):
 *   - role="dialog" + aria-modal="true" + aria-labelledby (+ optional
 *     aria-describedby) → assistive tech announces a named modal.
 *   - INITIAL FOCUS moves INTO the dialog on open: to `initialFocusRef` when
 *     it can take focus, else the first focusable descendant, else the dialog
 *     container itself — a keyboard/SR user starts inside, never stranded
 *     behind it. The move is synchronous in a mount effect (declared AFTER
 *     useRestoreFocus, so the opener is captured first) — no setTimeout, so a
 *     caller can assert the focused control right after the dialog opens.
 *   - FOCUS TRAP: Tab / Shift+Tab cycle within the dialog (last→first,
 *     first→last); focus can't fall back into the page behind the modal —
 *     acute under timed-exam conditions. The browser moves focus itself, so
 *     every control keeps its own Tab stops (the month, day and year fields of
 *     a date input; a radio group's single stop), and an empty focusable GUARD
 *     just before and just after the dialog element catches the Tab that
 *     crosses an edge and sends focus round (see FOCUS GUARDS below). Focus
 *     the dialog lost to <body> (its control was disabled mid-request or
 *     unmounted) goes on from there and stays in; focus that page code moved
 *     onto the page behind comes back on the next Tab.
 *   - ESCAPE closes (calls onClose). Capture-phase + stopPropagation so it
 *     doesn't also trip a background keydown handler.
 *   - STACKING: dialogs can be open two at once (the COPPA age gate raised
 *     over the Progress walkthrough by the walkthrough's own tutor request).
 *     The dialog opened LAST is on top: it alone acts on Escape and Tab, and
 *     it paints on top, its scrim lifting above any open dialog on the same
 *     or a higher z-index tier; see modalStack.js.
 *   - RESTORE FOCUS: on unmount, focus returns to the element that opened the
 *     dialog (useRestoreFocus). When it can't: into the dialog left on top if
 *     one is still open (onto the control the user was last on there, else
 *     its initial target); else, when the dialog's own action left that opener
 *     unable to take focus (disabled while its request runs, or removed), to
 *     `returnFocusRef`; failing both — or when no opener ever existed
 *     (interceptor-driven opens) — to #main-content.
 *
 * SCRIM (handleScrimPress, below): a primary press without Ctrl that
 * STARTS on the backdrop calls onClose, so a drag that starts on the card and
 * releases outside does NOT dismiss, and neither does a right-click or a macOS
 * ctrl-click. No press on the backdrop ever moves focus: its default mousedown
 * action would otherwise blur the opener that the close has just restored
 * focus to. Keyboard users use Escape.
 *
 * STYLING stays with each call site: pass `overlayClassName` (the scrim) and
 * `className` (the card) so adopting this primitive is a behavior change, not a
 * visual one. The `ui-modal-overlay` / `ui-modal` defaults (Modal.css) only keep
 * a bare <Modal> usable.
 *
 * Props:
 *   labelledBy        id of the element naming the dialog → aria-labelledby.
 *   describedBy       optional id → aria-describedby.
 *   onClose           () => void — Escape + scrim dismiss (the cancel path).
 *   initialFocusRef   optional ref to the element to focus on open.
 *   returnFocusRef    optional ref to where focus goes on close when the opener
 *                     can no longer take it: a confirm that disables its opener
 *                     while the request runs, or removes it. Point it at the
 *                     section heading or status line that continues the user's
 *                     work, rendered with tabIndex={-1} (never stamped for you).
 *                     Without it that close lands on #main-content, whose next
 *                     Tab jumps to the top of the page. Unused while the opener
 *                     can take focus (Cancel / Escape) and when the confirm
 *                     handler already put focus somewhere live.
 *   overlayClassName  scrim className (default 'ui-modal-overlay').
 *   className         card className (default 'ui-modal').
 *   closeOnEscape     default true.
 *   closeOnScrimClick default true.
 *   busy              default false. Pass true while the dialog's own request
 *                     is in flight (a save, a create, a destructive confirm)
 *                     and can't be taken back: Escape and a scrim press then
 *                     call nothing. Escape is still consumed, so it can't
 *                     act on the page behind, and a scrim press still never
 *                     moves focus. A dialog's Cancel button being disabled
 *                     stops neither path, since both reach onClose without it.
 *                     closeOnEscape={false} is not a substitute: it hands
 *                     Escape to whatever else listens (content with its own
 *                     Escape handling) instead of holding it. A busy dialog has
 *                     no exit until its request settles, so give that request
 *                     a deadline (config.js HELD_REQUEST_TIMEOUT_MS). Pair
 *                     `busy` with ./busyForm.js (the controls keep focus) and
 *                     a <PendingStatus> (the wait is announced); don't mirror
 *                     it into aria-busy on the dialog, which would hold that
 *                     announcement back.
 *   children          dialog content (must include the labelledBy element).
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import useRestoreFocus, { canTakeFocusNow } from '../../coppa/useRestoreFocus';
import { openModals, pushModal, topModal } from './modalStack';
import './Modal.css';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, summary, ' +
  '[contenteditable]:not([contenteditable="false"]), ' +
  '[tabindex]:not([tabindex="-1"])';

/**
 * Tabbable descendants of `container`, in DOM order.
 *
 * INCLUDES: button, [href], input, select, textarea, summary (the native
 *   <details> disclosure toggle), [contenteditable] (unless ="false"), and any
 *   element with [tabindex] other than "-1".
 * EXCLUDES: [disabled], aria-hidden="true", [hidden], and a dialog's focus
 *   guards (those of a <Modal> nested in `container`): never a control.
 *
 * OUT OF SCOPE — CSS-driven invisibility (display:none / visibility:hidden) is
 * NOT filtered: jsdom has no layout engine, so an offsetParent/getComputedStyle
 * probe would be unreliable here and give false confidence. The list is not the
 * browser's Tab order either (a radio group counts every radio; a date input,
 * one stop). The dialog uses it to choose where to PUT focus, never staying on
 * a pick that refuses it, and to know when no control can take Tab at all.
 * Where Tab goes from a control is the browser's call (FOCUS GUARDS).
 */
export function getFocusables(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      !el.hasAttribute('hidden') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      !el.hasAttribute('data-modal-guard'),
  );
}

/**
 * Move focus into `dialog`: onto `preferred` when it takes focus, else the
 * first tabbable descendant, else the dialog container itself (given
 * tabindex="-1") so the dialog is still announced and Escape still reaches its
 * handler. Runs on open, when a guard of a dialog under this one hands focus
 * up (handleGuardFocus), and as the fallback of focusResumeTarget.
 */
function focusInitialTarget(dialog, preferred) {
  if (preferred && typeof preferred.focus === 'function') {
    preferred.focus();
    // A disabled target refuses focus: fall through rather than leave focus
    // on the page behind the dialog.
    if (document.activeElement === preferred) return;
  }
  const focusables = getFocusables(dialog);
  if (focusables.length > 0) {
    focusables[0].focus();
    // getFocusables can't see CSS-hidden controls (header), and one refuses
    // focus: fall through to the container rather than stay behind.
    if (document.activeElement === focusables[0]) return;
  }
  focusContainer(dialog);
}

/**
 * Take focus back into `dialog` when a dialog stacked above it closes with no
 * opener to return to (useRestoreFocus): onto `resumePoint`, the control the
 * user was last on in this dialog, when it can take focus now; else as on open
 * (focusInitialTarget).
 *
 * The initial target alone is the wrong landing. It is where a dialog nobody
 * has touched yet starts, and the user of this one was somewhere: Settings'
 * first switch is far from the state-standards picker whose consent 403 raised
 * the parent-invite. document.activeElement cannot say where they were, since
 * Chromium moves focus to <body> once the focused control is disabled; the
 * last focus event in the dialog can.
 */
function focusResumeTarget(dialog, resumePoint, preferred) {
  // Nothing remembered yet, or it is gone, disabled or hidden by now.
  if (canTakeFocusNow(resumePoint)) {
    resumePoint.focus();
    // Whatever canTakeFocusNow cannot see (a CSS-hidden control in a browser
    // without checkVisibility) refuses focus: fall through.
    if (document.activeElement === resumePoint) return;
  }
  focusInitialTarget(dialog, preferred);
}

/**
 * Focus the dialog container itself, given tabindex="-1" so it can take focus
 * without entering the Tab order: the landing when no control can take focus.
 */
function focusContainer(dialog) {
  if (!dialog) return;
  if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
  dialog.focus();
}

/**
 * Focus the dialog's first tabbable, or its last when `backward`, passing over
 * any that refuses focus (CSS-hidden: getFocusables can't tell), else the
 * container. Where a Tab that crossed an edge of the dialog comes back in.
 */
function focusFromEdge(dialog, backward) {
  const focusables = getFocusables(dialog);
  if (backward) focusables.reverse();
  for (const el of focusables) {
    el.focus();
    if (document.activeElement === el) return;
  }
  focusContainer(dialog);
}

// FOCUS GUARDS. The trap used to cancel the browser's Tab whenever
// document.activeElement was the first (Shift+Tab) or last (Tab) tabbable, and
// focus the other end itself. That misreads any control holding several Tab
// stops of its own while activeElement stays on it: Chromium's
// <input type="date"> has month, day, year and a picker button, so in the age
// gate (the date first) Shift+Tab from the day field jumped to "Not now", and a
// date input last could never be Tabbed past its month. getFocusables' order
// isn't the browser's either (a radio group is one stop, its checked radio),
// and from a stop the trap didn't know as an edge, Tab left the dialog.
//
// So the browser now runs every Tab from inside the dialog, and these two
// elements, just before and just after the dialog element, are where a Tab
// that crosses an edge lands: the guard sends focus round within the same
// focus event. Screen readers: a guard is empty, with no role and no name;
// after a wrap, Chromium's accessibility tree has focus on the control, not
// the guard (measured over CDP; no screen reader was run). They sit outside
// role="dialog", so a reader that honours aria-modal skips them with the
// page behind (Chromium on Windows leaves that to the reader: its tree keeps
// both). They are deliberately NOT aria-hidden: a focusable element must
// never be (axe's aria-hidden-focus rule, WCAG 4.1.2). axe's experimental,
// off-by-default focus-order-semantics rule does flag them, for sitting in
// the Tab order with no role; that is what a guard is. Fixed and zero-height,
// they take no space in the overlay's flex layout, and pointer-events: none
// keeps every press on the scrim itself.
const GUARD_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 1,
  height: 0,
  margin: 0,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
};

/**
 * The one rule for a press on the scrim (the dimmed backdrop). It lived in its
 * own module, ./scrimPress.js, while SettingsDialog, the Classrooms create
 * modal and the COPPA PlatformConsentScreen still hand-rolled their overlays,
 * so the rule couldn't drift between copies again (it had: none of those
 * scrims had rule 2). They all render through <Modal> now, so its one caller is
 * the scrim below.
 *
 *   1. Only a press that STARTS on the scrim counts (target === currentTarget),
 *      so a text drag that begins on the card and ends outside never closes it.
 *   2. The press never moves focus. preventDefault() cancels the browser's
 *      default mousedown action, which blurs whatever holds focus when the
 *      press lands on non-focusable content. The browser runs that default
 *      AFTER the handlers: after a dismiss has unmounted the dialog and
 *      useRestoreFocus has already put focus back on the opener (or on
 *      #main-content). Left alone, it undid that restore and stranded the user
 *      at <body>; on a press that doesn't dismiss, it pulled focus out of the
 *      open dialog. Cancelling it also stops a drag or middle-button autoscroll
 *      from starting on the scrim. It doesn't cancel the click that follows,
 *      and doesn't suppress a context menu.
 *   3. Only a primary press without Ctrl dismisses. A right-click, a pen's
 *      barrel button, a middle-click, or a macOS ctrl-click (a secondary click
 *      there) leaves the dialog open. Ctrl is ignored on every platform for
 *      simplicity; Shift-, Alt- and Cmd-presses still dismiss.
 *
 * `onDismiss` is omitted for a scrim that must not dismiss
 * (closeOnScrimClick={false}, or a `busy` dialog). Rule 2 still applies.
 */
function handleScrimPress(event, onDismiss) {
  if (event.target !== event.currentTarget) return;
  event.preventDefault();
  if (event.button !== 0 || event.ctrlKey) return;
  onDismiss?.();
}

/** The z-index an overlay paints at, or null when none is set (auto). */
function zIndexOf(el) {
  if (!el) return null;
  const z = Number.parseInt(getComputedStyle(el).zIndex, 10);
  return Number.isNaN(z) ? null : z;
}

// Paint order follows the modal stack, as keyboard ownership does: the dialog
// opened last paints on top. The z-index tiers order the usual case (a gate
// opening over the page dialog whose request raised it) and need no lift. This
// covers what tiers can't see:
//   - two dialogs on ONE tier: CheckoutFlow, inside the upgrade dialog, asks for
//     the date of birth; both scrims sit on --z-system-overlay and App.js
//     renders the upgrade host AFTER the gate host, so DOM order painted the
//     date-of-birth dialog under the dialog it opened over;
//   - a page dialog that opens by itself over a gate: PrepPath's countdown
//     auto-submit mounts the score summary. It is the top for the keyboard, so
//     it paints above the gate too (the <dialog>.showModal() top-layer rule).
// The comparison assumes both scrims paint in the root stacking context, as
// every top-level overlay does. Exceptions stay harmless: a dialog nested in
// another's card lifts only inside that card's context, and the exam shell's
// own dialogs render inside .dsat-exam-shell's context (root-level 1000), so
// no lift there can clear a gate; both open only from clicks a gate blocks.
function liftAboveOpenDialogs(overlay, self) {
  const others = openModals().filter((entry) => entry !== self);
  if (others.length === 0) return;
  const own = zIndexOf(overlay);
  if (own === null) return;
  let highest = null;
  others.forEach((entry) => {
    const z = entry.zIndex();
    if (z !== null && (highest === null || z > highest)) highest = z;
  });
  if (highest !== null && highest >= own) overlay.style.zIndex = String(highest + 1);
}

// ── Page scroll-lock coordination (module scope) ──────────────────────────────
// While ≥1 Modal is open, the page behind it is held still: a background that
// scrolls under an open dialog is disorienting, and on touch it lets the user
// scroll the inert page instead of the dialog. Coordinated by an open-COUNT (not
// a boolean) so nested or rapidly-swapped modals don't restore the page early —
// only the LAST close restores. The ORIGINAL inline values are captured once (on
// the 0→1 transition) and written back verbatim once (on 1→0), so a page that had
// deliberately set one of them inline gets exactly its value back.
//
// WHY <html>, NOT <body>: the viewport scrolls per the ROOT element's overflow
// and only borrows <body>'s while <html>'s is `visible` on both axes (CSS
// Overflow 3 viewport propagation). App.css and reset.css both set
// `html { overflow-x: hidden }`, so the old `body.style.overflow = 'hidden'` never
// reached the viewport: the page kept scrolling behind every dialog and the root
// scrollbar never went away. Writing the root locks the viewport whatever the
// stylesheets say about html/body. (Not `document.scrollingElement`: in quirks
// mode that is <body> — the same dead end.) Longhands, not the `overflow`
// shorthand: the shorthand reads back '' whenever only one axis is set inline, so
// a save/restore through it would drop a lone inline `overflow-y`.
//
// NO LAYOUT SHIFT: hiding the root's overflow removes a classic (space-taking)
// scrollbar, which would widen the page and jump everything under the scrim
// sideways. `scrollbar-gutter: stable` keeps that strip reserved while locked, so
// in-flow AND fixed-position content stay put. It is set only when a classic
// scrollbar is showing at lock time — measured BEFORE the overflow write, while
// it is still laid out — because reserving a gutter on a page that had no
// scrollbar would shift it the other way. Overlay scrollbars (macOS default,
// iOS, Android) take no width, measure 0, and need nothing kept. The gutter goes
// on the root too: the CSSWG resolved that scrollbar-gutter, unlike overflow,
// never propagates to the viewport from <body>. Accepted costs: the reserved
// strip lies outside every fixed scrim, so where the (equally undimmed)
// scrollbar was, it shows plain page background; and a browser without
// scrollbar-gutter (Safari < 18.2 with always-on scrollbars) still shifts by the
// scrollbar width.
//
// iOS SAFARI: root `overflow: hidden` has blocked touch scrolling since iOS 13
// (WebKit r245006; home-screen web apps since r293188), but WebKit still lets the
// page pan whenever the visual viewport is smaller than the layout viewport —
// pinch-zoomed, on-screen keyboard up for a dialog's text field, some toolbar
// states (WebKit bugs 240860 / 236561, open). Accepted rather than worked around:
// `position: fixed` on <body> (a scroll-offset save/restore that jumps when they
// disagree, broken `position: sticky`, fights with toolbar resizes) and a
// non-passive touchmove blocker (no pinch-zoom inside the dialog) both cost more
// than the leak — and WebKit leaks on purpose, so a zoomed-in reader can always
// pan to the dialog.
//
// OWNERSHIP INVARIANT (review #462 LOW): this counter is the SOLE writer of
// <html>'s inline `overflow-x`, `overflow-y` and `scrollbar-gutter` in the app.
// Any future surface that needs to lock page scroll (lightbox, drawer, route
// overlay) must route through this counter — writing those properties directly
// would be silently clobbered by the last Modal close restoring `savedRootStyle`.
let openModalCount = 0;
let savedRootStyle = null;

function lockPageScroll() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  if (openModalCount === 0) {
    const root = document.documentElement;
    const { style } = root;
    // Capture the prior inline values (usually '') BEFORE clobbering them.
    savedRootStyle = {
      overflowX: style.overflowX,
      overflowY: style.overflowY,
      scrollbarGutter: style.scrollbarGutter,
    };
    // A classic scrollbar is showing iff it takes layout width. Read it now: once
    // the overflow below is hidden the scrollbar is gone and this reads 0.
    if (window.innerWidth > root.clientWidth) style.scrollbarGutter = 'stable';
    style.overflowX = 'hidden';
    style.overflowY = 'hidden';
  }
  openModalCount += 1;
}

function unlockPageScroll() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  // Double-restore guard: never decrement below zero or restore twice.
  if (openModalCount === 0) return;
  openModalCount -= 1;
  if (openModalCount === 0) {
    Object.assign(document.documentElement.style, savedRootStyle);
  }
}

export default function Modal({
  labelledBy,
  describedBy,
  onClose,
  initialFocusRef,
  returnFocusRef,
  overlayClassName = 'ui-modal-overlay',
  className = 'ui-modal',
  closeOnEscape = true,
  closeOnScrimClick = true,
  busy = false,
  children,
}) {
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  // The resume point (focusResumeTarget): the control that took focus last in
  // this dialog, remembered after it loses focus.
  const resumePointRef = useRef(null);
  // This dialog's entry on the modal stack (modalStack.js): one object for the
  // component's lifetime, whose accessors read the refs when called.
  // initialFocusRef is a stable ref object.
  const stackEntry = useRef(null);
  if (stackEntry.current === null) {
    stackEntry.current = {
      isOpen: () => Boolean(dialogRef.current?.isConnected),
      contains: (node) => Boolean(node && dialogRef.current?.contains(node)),
      focusInitial: () => focusInitialTarget(dialogRef.current, initialFocusRef?.current),
      focusResume: () =>
        focusResumeTarget(dialogRef.current, resumePointRef.current, initialFocusRef?.current),
      zIndex: () => zIndexOf(overlayRef.current),
    };
  }
  // Remembered only while this dialog is the one on top, the one the user is
  // in. Focus in a dialog nested in this one's content bubbles through here
  // too, on its controls and its focus guards, all gone once it closes. (A
  // nested dialog joins the stack before its own initial focus. Content of it
  // that took focus earlier still, from autoFocus or a mount effect of its
  // own, would be remembered here; none does.)
  const rememberFocus = (e) => {
    if (topModal() === stackEntry.current) resumePointRef.current = e.target;
  };

  // Paint above every dialog already open (liftAboveOpenDialogs). A layout
  // effect, so the lift lands before the first paint.
  useLayoutEffect(() => {
    liftAboveOpenDialogs(overlayRef.current, stackEntry.current);
  }, []);

  // Join the modal stack for this dialog's lifetime. Declared BEFORE
  // useRestoreFocus: on close, effect cleanups run in declaration order, so
  // this dialog is off the stack by the time the restore asks which dialog is
  // left open. StrictMode's mount→cleanup→mount stays balanced: the entry
  // leaves and rejoins on top.
  useEffect(() => pushModal(stackEntry.current), []);

  // Restore focus to the opener (else into the dialog left open on top, else
  // returnFocusRef, else #main-content) on close — WCAG 2.4.3. Declared before
  // the initial-focus effect so it captures document.activeElement before
  // focus moves into the dialog.
  useRestoreFocus({ fallbackRef: returnFocusRef });

  // Move initial focus INTO the dialog on open. Synchronous (no setTimeout) so
  // callers can assert the focused control immediately after open.
  useEffect(() => {
    focusInitialTarget(dialogRef.current, initialFocusRef?.current);
    // initialFocusRef is a stable ref object; open-once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the LATEST onClose / closeOnEscape in a ref so the keydown subscription
  // below can stay mounted-once instead of tearing down + re-adding the document
  // listener on every parent render. Every adopter passes an inline `onClose`,
  // whose identity changes each render (e.g. every keystroke in AgeGateModal's
  // date field) — re-subscribing on each of those was needless churn on a hot,
  // document-global listener. `busy` rides the same ref, read just as live.
  const escapeCfg = useRef({ onClose, closeOnEscape, busy });
  useEffect(() => {
    escapeCfg.current = { onClose, closeOnEscape, busy };
  });

  // The Tab key press this dialog is handling, from its keydown to its keyup,
  // else null: which way it goes ({ backward }, read by the guards and the
  // focusin check below) and where the page was scrolled when it began. A key
  // press, not a timer: the browser moves focus for Tab somewhere in between
  // (Chromium right after the keydown, measured; other engines weren't). The
  // document never gets the keyup when the window loses focus mid-press, so
  // the window's blur ends the press too.
  const tabPress = useRef(null);

  // A guard took focus: the browser's Tab crossed an edge of the dialog. Send
  // focus round, forward to the first control and backward to the last. With
  // no Tab under way (a script or an assistive technology moved focus here),
  // the guard's own side decides: past the end means round to the start.
  const handleGuardFocus = (guard, edge) => {
    // Focus already moved on while this focus event was still being
    // delivered: the top dialog's focusin check (below) runs first, from the
    // document, and it takes back a Tab that landed on a guard of a dialog
    // under it. Acting again would undo its choice of end.
    if (document.activeElement !== guard) return;
    const top = topModal();
    if (top !== stackEntry.current) {
      // A guard of a dialog under the top one: not reachable by Tab from the
      // top dialog, whose own guards sit right next to it. Hand focus up.
      top?.focusInitial();
      return;
    }
    const backward = tabPress.current ? tabPress.current.backward : edge === 'start';
    focusFromEdge(dialogRef.current, backward);
  };

  // Focus trap + Escape-to-close (document capture phase, plus the window's
  // own blur). Subscribes ONCE on mount and reads the live onClose /
  // closeOnEscape / busy through escapeCfg, so the handler identity never
  // changes. Escape, on the top dialog only: busy keeps the key and closes
  // nothing; otherwise closeOnEscape decides. On, the key is kept
  // (stopPropagation) and onClose runs; off, it passes through untouched.
  useEffect(() => {
    const handleKey = (e) => {
      // Every open Modal's listener sees every key, so each defers unless it is
      // the dialog on top: Escape in the age gate closes the gate alone, not
      // also the walkthrough beneath it, and only the gate traps Tab.
      if (topModal() !== stackEntry.current) return;
      if (e.key === 'Escape') {
        // Busy keeps the key and closes nothing. Letting it through instead,
        // as closeOnEscape={false} does, would hand it to the page behind a
        // dialog that is mid-request.
        if (escapeCfg.current.busy) {
          e.stopPropagation();
          return;
        }
        if (!escapeCfg.current.closeOnEscape) return;
        e.stopPropagation();
        escapeCfg.current.onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      tabPress.current = {
        backward: e.shiftKey,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      };
      const dialog = dialogRef.current;
      const focusables = getFocusables(dialog);
      if (focusables.length === 0) {
        // F13 PR4.3 / adversarial-review S4 (MEDIUM) — a PENDING state (e.g.
        // a confirm dialog whose buttons both disable while a destructive
        // request is in flight) can leave ZERO focusable descendants. The
        // trap has nothing to bounce Tab BETWEEN in that case — but doing
        // NOTHING here previously let the browser's NATIVE Tab handling take
        // over and walk focus OUT of the dialog into the page behind it,
        // defeating the whole point of a modal while it's mid-action. Contain
        // it instead: prevent the native Tab and keep focus ON the dialog
        // container (already a valid target — see the initial-focus effect
        // above, which sets the SAME `tabindex="-1"` fallback for the
        // zero-focusables-at-MOUNT case; this applies the identical fallback
        // DYNAMICALLY, whenever content that WAS focusable becomes disabled
        // after mount). Fixed at this shared primitive (not per-caller) so
        // every dialog built on Modal gets the fix for free. (A dialog on
        // busyForm.js never gets here: its field and button stay focusable.)
        e.preventDefault();
        if (document.activeElement !== dialog) focusContainer(dialog);
        return;
      }
      const active = document.activeElement;
      const lost = !active || active === document.body || active === document.documentElement;
      if (!lost && !overlayRef.current?.contains(active)) {
        // Focus is on the page behind this dialog, or in a dialog under it:
        // page code moved it there. Take the key and bring focus back in.
        e.preventDefault();
        focusFromEdge(dialog, e.shiftKey);
      }
      // Otherwise the browser runs the Tab. From inside the dialog it moves
      // focus through the controls' own stops and, at an edge, onto a guard.
      // From <body> (focus lost: its control was disabled mid-request or
      // unmounted), Chromium resumes from where that control was, so this
      // too stays in or reaches a guard; the focusin check below catches a
      // browser that starts from the top of the page instead.
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Tab') tabPress.current = null;
    };
    // The window lost focus with Tab still down (the user switched windows
    // mid-press): its keyup goes elsewhere. Left set, that press would pick the
    // direction for a guard focused later with no key at all (an assistive
    // technology's own "next control", a script): after a stale Shift+Tab,
    // focus arriving past the end went back to the last control every time.
    // Once the press has ended, focus that comes back behind the dialog waits
    // for the next Tab like any other (LIMITS below).
    // A control's own blur doesn't bubble, so only the window's reaches this
    // bubble-phase listener. The target check is a guard for that: in the
    // capture phase every control's blur would end the press before that
    // Tab's focusin.
    const handleWindowBlur = (e) => {
      if (e.target === window) tabPress.current = null;
    };
    // A Tab from <body> that doesn't resume at the lost control can still
    // take focus out of the dialog. Measured in Chromium 152, it does resume
    // (a failed age-gate submit from the date's month field, then Shift+Tab:
    // the guard before the dialog). But not every engine keeps that spot, and
    // a script can leave it on the page (it focused a page control, then
    // blurred it: Tab went on to the next page control). So if a Tab this
    // dialog saw lands focus outside it, bring it back. The browser has
    // already scrolled the page to the control it reached (Chromium does that
    // before the focus events, measured: a control 3000px down took the
    // locked page to its bottom), so put the page back where the keydown
    // found it. A scroller inside the page that moved too is not restored.
    //
    // LIMITS: focus that arrives behind the dialog with no Tab the document
    // saw stays there until the next Tab (the branch above): a Tab pressed in
    // the browser's own UI to come back into the page, a phone keyboard's
    // next-field key, a script's focus(). Keeping focus out for good takes an
    // inert background or <dialog>.showModal(), which this primitive doesn't
    // use yet.
    const handleFocusIn = (e) => {
      const press = tabPress.current;
      if (!press || topModal() !== stackEntry.current) return;
      if (overlayRef.current?.contains(e.target)) return;
      focusFromEdge(dialogRef.current, press.backward);
      if (window.scrollX !== press.scrollX || window.scrollY !== press.scrollY) {
        window.scrollTo(press.scrollX, press.scrollY);
      }
    };
    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('focusin', handleFocusIn, true);
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      window.removeEventListener('blur', handleWindowBlur);
    };
    // Mount-once: the live config is read via the escapeCfg ref, not through
    // deps, so the listeners are added/removed exactly on mount/unmount.
  }, []);

  // Page scroll-lock for the lifetime of this Modal. Ref-counted at module scope
  // (see lock/unlockPageScroll) so concurrent/rapidly-swapped modals restore the
  // page exactly once, to the values it had before the FIRST modal opened. The
  // mount/unmount pairing keeps it balanced (and StrictMode's mount→cleanup→mount
  // stays balanced through the counter, re-measuring the scrollbar after its
  // restore). This does NOT touch focus, so the trap/restore/Escape effects above
  // are unaffected.
  useEffect(() => {
    lockPageScroll();
    return () => unlockPageScroll();
  }, []);

  return (
    <div
      className={overlayClassName}
      ref={overlayRef}
      // The scrim rule (handleScrimPress above): only a press that starts on
      // the scrim counts, it never moves focus (the browser's default mousedown
      // blur would otherwise undo useRestoreFocus's restore), and only a
      // primary press without Ctrl dismisses.
      onMouseDown={(e) => handleScrimPress(e, closeOnScrimClick && !busy ? onClose : undefined)}
    >
      <span
        data-modal-guard="start"
        tabIndex={0}
        style={GUARD_STYLE}
        onFocus={(e) => handleGuardFocus(e.currentTarget, 'start')}
      />
      <div
        className={className}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        ref={dialogRef}
        onFocus={rememberFocus}
      >
        {children}
      </div>
      <span
        data-modal-guard="end"
        tabIndex={0}
        style={GUARD_STYLE}
        onFocus={(e) => handleGuardFocus(e.currentTarget, 'end')}
      />
    </div>
  );
}
